import structuredClone from '@ungap/structured-clone'
import { emit } from '../services/Controller'
import { StatusBar, Style } from '@capacitor/status-bar'
import { RootModel } from '.'
import { isDarkMode } from '../styling/theme'
import i18n, { resolveLanguage, LanguageMode } from '../i18n'
import { Settings as LuxonSettings } from 'luxon'
import { NoticeProps } from '../components/Notice'
import { createModel } from '@rematch/core'
import { SIDEBAR_WIDTH } from '../constants'
import { selectActiveAccountId } from '../selectors/accounts'
import browser, { getLocalStorage, setLocalStorage } from '../services/browser'

export const DEFAULT_INTERFACE = 'searching'

const SAVED_ACROSS_LOGOUT = [
  'apis',
  'panelWidth',
  'poppedBubbles',
  'expireBubbles',
  'guidesResetDate',
  'themeMode',
  'language',
  'accordion',
  'drawerMenu',
  'drawerAccordion',
  'collapsed',
  'columns',
  'columnWidths',
  'limitsOverride',
  'defaultService',
  'testUI',
  'updateNoticeCleared',
  'deviceTimeSeries',
  'serviceTimeSeries',
  'showDesktopNotice',
  'mobileWelcome',
  'logsFilters',
]

export type UIState = {
  themeMode: 'light' | 'dark' | 'system'
  themeDark: boolean
  language: LanguageMode
  testUI?: 'ON' | 'HIGHLIGHT'
  apis: {
    switchApi?: IPreferences['switchApi']
    apiGraphqlURL?: IPreferences['apiGraphqlURL']
    webSocketURL?: IPreferences['webSocketURL']
    apiURL?: IPreferences['apiURL']
  }
  layout: ILayout
  silent: string | null
  selected: IDevice['id'][]
  selectionAnchor?: IDevice['id']
  connected: boolean
  offline?: { severity: NoticeProps['severity']; title: string; message: NoticeProps['children'] }
  waitMessage?: string
  claiming: boolean
  fetching: boolean
  destroying: boolean
  transferring: boolean
  deleteAccount: boolean
  routingLock?: IRouteType
  routingMessage?: string
  sidebarMenu: boolean
  drawerMenu: 'FILTER' | 'COLUMNS' | null
  drawerAccordion: string | number
  columns: string[]
  columnWidths: ILookup<number>
  collapsed: string[]
  limitsOverride: ILookup<boolean>
  globalTooltip?: IGlobalTooltip
  defaultService: ILookup<string | null>
  defaultSelection: ILookup<ILookup<string | undefined>>
  registrationCommand?: string
  registrationCode?: string
  redirect?: string
  restoring: boolean
  scanEnabled: boolean
  scanLoading: { [interfaceName: string]: boolean }
  scanTimestamp: { [interfaceName: string]: number }
  scanInterface: string
  setupBusy: boolean
  setupAdded?: Partial<IService>
  setupRegisteringDevice: boolean
  setupDeletingDevice: boolean
  setupAddingService: boolean
  setupDeletingService?: string
  setupServiceBusy?: string
  setupServicesCount: number
  setupServicesNew: boolean
  setupServicesLimit: number
  successMessage: React.ReactNode
  noticeMessage: React.ReactNode
  errorMessage: React.ReactNode
  panelWidth: ILookup<number>
  poppedBubbles: string[]
  /** WHEN the user last chose "dismiss all", as a timestamp — not whether they did.
   *  A bubble introduced after that moment still gets shown. `true` is the legacy
   *  value from when this was a permanent flag; GuideBubble reads it as a dismissal
   *  dated to the release that changed this. */
  expireBubbles: number | boolean
  /** WHEN the user last chose "Reset interactive guides". The cohort gate uses
   *  max(account created, this) — so an explicit reset re-onboards even accounts
   *  that predate the guides, while guides cohort-dated after the reset stay gated. */
  guidesResetDate?: number
  confirm?: { id: string; callback: () => void }
  accordion: ILookup<boolean>
  autoConnect: boolean
  autoLaunch?: string
  autoCopy: boolean
  updateNoticeCleared?: number
  showRestoreModal: boolean
  deviceTimeSeries?: ITimeSeriesOptions
  serviceTimeSeries?: ITimeSeriesOptions
  connectThisDevice: boolean
  mobileWelcome: boolean
  showDesktopNotice: boolean
  scriptForm?: IFileForm
  scriptRunForms: ILookup<IFileForm>
  viewAsUser: { id: string; email: string } | null
  logsFilters: LogsFiltersByAccount
  announcementPresentationTest?: number
}

export const defaultState: UIState = {
  themeMode: 'system',
  themeDark: isDarkMode(),
  language: 'system',
  testUI: undefined,
  apis: {},
  layout: {
    mobile: false,
    showOrgs: false,
    hideSidebar: false,
    singlePanel: false,
    triplePanel: false,
    sidePanelWidth: SIDEBAR_WIDTH,
    showBottomMenu: false,
    insets: { top: 0, left: 0, bottom: 0, right: 0, topPx: '', bottomPx: '', leftPx: '', rightPx: '' },
  },
  silent: null,
  selected: [],
  selectionAnchor: undefined,
  connected: false,
  offline: undefined,
  waitMessage: undefined,
  claiming: false,
  fetching: false,
  destroying: false,
  transferring: false,
  deleteAccount: false,
  routingLock: undefined,
  routingMessage: undefined,
  sidebarMenu: false,
  drawerMenu: null,
  drawerAccordion: 'sort',
  columns: [
    'deviceName',
    'status',
    'deviceTimeSeries',
    'tags',
    'services',
    'serviceName',
    'serviceState',
    'serviceStatus',
    'serviceAction',
    'serviceTimeSeries',
  ],
  columnWidths: {},
  collapsed: [],
  limitsOverride: {},
  globalTooltip: undefined,
  defaultService: {},
  defaultSelection: {},
  registrationCommand: undefined,
  registrationCode: undefined,
  redirect: undefined,
  restoring: false,
  scanEnabled: true,
  scanLoading: {},
  scanTimestamp: {},
  scanInterface: DEFAULT_INTERFACE,
  setupBusy: false,
  setupAdded: undefined,
  setupRegisteringDevice: false,
  setupDeletingDevice: false,
  setupServiceBusy: undefined,
  setupDeletingService: undefined,
  setupAddingService: false,
  setupServicesCount: 0,
  setupServicesNew: true,
  setupServicesLimit: 100,
  successMessage: '',
  noticeMessage: '',
  errorMessage: '',
  panelWidth: {},
  poppedBubbles: [],
  expireBubbles: false,
  guidesResetDate: undefined,
  accordion: { config: false, configConnected: false, options: false, service: false, networks: false, logs: false },
  confirm: undefined,
  autoConnect: false,
  autoLaunch: undefined,
  autoCopy: false,
  updateNoticeCleared: undefined,
  showRestoreModal: false,
  deviceTimeSeries: undefined,
  serviceTimeSeries: undefined,
  connectThisDevice: false,
  mobileWelcome: true,
  showDesktopNotice: true,
  scriptForm: undefined,
  scriptRunForms: {},
  viewAsUser: null,
  logsFilters: {},
  announcementPresentationTest: undefined,
}

export default createModel<RootModel>()({
  state: { ...defaultState },
  effects: dispatch => ({
    async init() {
      console.log('UI INIT')
      // add color scheme listener
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
        dispatch.ui.setTheme(undefined)
      })
      await dispatch.ui.restoreState()
    },
    async restoreState(_: void, state) {
      let states: ILookup<any> = {}
      SAVED_ACROSS_LOGOUT.forEach(key => {
        const value = getLocalStorage(state, `ui-${key}`)
        if (value !== null) {
          if (typeof value === 'object' && !Array.isArray(value)) states[key] = { ...state.ui[key], ...value }
          else states[key] = value
        }
      })
      console.log('RESTORE UI STATE', states)
      states = migrateColumnStates(states)
      dispatch.ui.set(states)
      dispatch.ui.setTheme(states.themeMode)
      dispatch.ui.setLanguage(states.language)
    },
    async setupUpdated(count: number, state) {
      if (count !== state.ui.setupServicesCount) {
        dispatch.ui.updated()
        dispatch.ui.set({ setupServicesCount: count, setupAdded: undefined, setupServicesNew: true })
      }
    },
    async setTheme(themeMode: UIState['themeMode'] | void, state) {
      themeMode = themeMode || state.ui.themeMode
      const themeDark = isDarkMode(themeMode)
      dispatch.ui.setPersistent({ themeMode })
      dispatch.ui.set({ themeDark })
      if (browser.isAndroid) StatusBar.setStyle({ style: themeDark ? Style.Dark : Style.Light })
    },
    async setLanguage(language: UIState['language'] | void, state) {
      language = language || state.ui.language
      const resolved = resolveLanguage(language)
      dispatch.ui.setPersistent({ language })
      LuxonSettings.defaultLocale = resolved
      if (i18n.resolvedLanguage !== resolved) i18n.changeLanguage(resolved)
      // Sync the resolved code to the Electron main process (tray/menu/dialogs).
      if (browser.isElectron) {
        const { preferences } = state.backend
        if (preferences.language !== resolved) emit('preferences', { language: resolved })
      }
    },
    async resizeColumn(params: { id: string; width: number }, state) {
      const columnWidths = { ...state.ui.columnWidths, [params.id]: params.width }
      console.log('SET COLUMN WIDTHS', columnWidths)
      dispatch.ui.setPersistent({ columnWidths })
    },
    async pop(bubble: string, state) {
      let poppedBubbles = [...state.ui.poppedBubbles]
      poppedBubbles.push(bubble)
      dispatch.ui.setPersistent({ poppedBubbles })
    },
    async popAll(_: void) {
      // Dated, so this only opts out of the guides that exist TODAY — a single
      // click should not silently cancel every future feature's onboarding.
      dispatch.ui.setPersistent({ expireBubbles: Date.now() })
    },
    async resetHelp(_: void) {
      dispatch.ui.setPersistent({
        poppedBubbles: [...defaultState.poppedBubbles],
        expireBubbles: false,
        // An explicit reset treats the account as new from this moment, so even
        // accounts that predate the guides get onboarded again
        guidesResetDate: Date.now(),
      })
    },
    async accordion(params: ILookup<boolean>, state) {
      const accordion = { ...state.ui.accordion, ...params }
      dispatch.ui.setPersistent({ accordion })
    },
    async setDefaultService({ deviceId, serviceId }: { deviceId: string; serviceId: string | null }, state) {
      const all = { ...state.ui.defaultService }
      all[deviceId] = serviceId
      dispatch.ui.setPersistent({ defaultService: all })
    },
    async setDefaultSelected({ key, value, accountId }: { key: string; value?: string; accountId?: string }, state) {
      const id = accountId || selectActiveAccountId(state)
      let defaultSelection = structuredClone(state.ui.defaultSelection)
      defaultSelection[id] = defaultSelection[id] || {}
      defaultSelection[id][key] = value
      dispatch.ui.set({ defaultSelection })
    },
    async setLogsFilter(
      {
        accountId,
        context,
        eventTypes,
      }: {
        accountId?: string
        context: LogsFilterContext
        eventTypes?: IEventType[]
      },
      state
    ) {
      const id = accountId || selectActiveAccountId(state)
      if (!id) return

      const logsFilters = structuredClone(state.ui.logsFilters)
      logsFilters[id] = logsFilters[id] || {}
      logsFilters[id][context] = { eventTypes }
      dispatch.ui.setPersistent({ logsFilters })
    },
    async setPersistent(params: ILookup<any>, state) {
      dispatch.ui.set(params)
      Object.keys(params).forEach(key => {
        if (SAVED_ACROSS_LOGOUT.includes(key)) setLocalStorage(state, `ui-${key}`, params[key])
      })
    },
    async deprecated() {
      if (!browser.isElectron) return
      dispatch.ui.set({
        errorMessage: i18n.t('notices:version.unsupported', {
          defaultValue: 'This version of Desktop is no longer supported. It should auto update shortly.',
        }),
      })
      emit('preferences', { autoUpdate: true })
    },
  }),
  reducers: {
    set(state: UIState, params: Partial<UIState>) {
      Object.keys(params).forEach(key => (state[key] = params[key]))
      return state
    },
    updated(state: UIState) {
      state.setupBusy = false
      state.setupAddingService = false
      state.setupServiceBusy = undefined
      state.restoring = false
      return state
    },
    reset(state: UIState) {
      state = { ...defaultState }
      return state
    },
  },
})

function migrateColumnStates(states: ILookup<any>): ILookup<any> {
  if (!states.columns || states.columns.includes('serviceName')) return states
  console.log('MIGRATE COLUMN STATES')
  return {
    ...states,
    columns: [...states.columns, 'serviceName', 'serviceStatus', 'serviceState', 'serviceAction', 'serviceTimeSeries'],
  }
}
